import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../product/ProductCard.css";
import { FaHeart } from "react-icons/fa";
import { AuthContext } from "../../routes/AuthContext";
import { toast } from "react-toastify";
import { addToWishlist, removeFromWishlist } from "../wishlist/Wishlist";
import { addToCart } from "../cart/Cart";

const TopSelling = () => {
  const [products, setProducts] = useState([]);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [addingId, setAddingId] = useState(null);
  const [cartQtyMap, setCartQtyMap] = useState({});
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const { getCustomerData, isAuthenticated } = useContext(AuthContext);
  const customer = getCustomerData();
  const customer_id = customer?.customer_id;

  /* =========================
     FETCH TOP SELLING
  ========================== */
  useEffect(() => {
    const fetchTopSelling = async () => {
      try {
        const res = await axios.post(`${BASE_URL}top_selling`);

        if (res.data.api_success) {
          const mappedProducts = res.data.data.flatMap((item) =>
            item.products.map((p) => ({
              id: p.product_id,
              product_name: p.product_name,
              selling_price: p.selling_price,
              cost_price: p.cost_price,
              images: p.images,
            }))
          );

          setProducts(mappedProducts);
        }
      } catch (error) {
        console.error("Top selling fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopSelling();
  }, [BASE_URL]);

  /* =========================
     FETCH WISHLIST IDS
  ========================== */
  useEffect(() => {
    if (!customer_id) return;

    const fetchWishlistIds = async () => {
      try {
        const formData = new FormData();
        formData.append("customer_id", customer_id);

        const res = await axios.post(
          `${BASE_URL}customer_wish_list_details`,
          formData
        );

        if (res.data.api_success) {
          const ids = res.data.wishlist_items.map(
            (item) => item.product_id
          );
          setWishlistIds(ids);
        }
      } catch (error) {
        console.error("Wishlist fetch error", error);
      }
    };

    fetchWishlistIds();
  }, [customer_id, BASE_URL]);

  /* =========================
     ADD TO CART
  ========================== */
  const handleAddToCart = async (e, product_id) => {
    e.stopPropagation();

    if (!isAuthenticated || !customer_id) {
      toast.warning("Please login to add items to cart");
      return;
    }

    if (addingId === product_id) return;

    try {
      setAddingId(product_id);

      const currentQty = cartQtyMap[product_id] || 0;
      const newQty = currentQty + 1;

      const res = await addToCart(customer_id, product_id, newQty);

      if (res.api_success) {
        setCartQtyMap((prev) => ({
          ...prev,
          [product_id]: newQty,
        }));

        toast.success(
          currentQty === 0 ? "Added to cart" : "Quantity increased"
        );
      } else {
        toast.error("Failed to add to cart");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setTimeout(() => setAddingId(null), 400);
    }
  };

  /* =========================
     ❤️ TOGGLE WISHLIST
  ========================== */
  const toggleWishlist = async (product_id) => {
    if (!isAuthenticated || !customer_id) {
      toast.warning("Please login to use wishlist");
      return;
    }

    const isWishlisted = wishlistIds.includes(product_id);

    if (isWishlisted) {
      await removeFromWishlist(customer_id, product_id);
      setWishlistIds((prev) => prev.filter((id) => id !== product_id));
      toast.info("Removed from wishlist");
    } else {
      await addToWishlist(customer_id, product_id);
      setWishlistIds((prev) => [...prev, product_id]);
      toast.success("Added to wishlist");
    }
  };

  if (loading) {
    return <p style={{ textAlign: "center" }}>Loading...</p>;
  }

  return (
    <div className="product-grid-container">
      <div className="category-header">
        <h2 className="category-heading">Top Selling</h2>
      </div>

      <div className="slider-wrapper">
        <div className="product-grid slider-mode">
          {products.map((item) => {
            const isWishlisted = wishlistIds.includes(item.id);

            return (
              <div
                key={item.id}
                className="product-card"
                onClick={() =>
                  navigate(`/product-details/${item.id}`)
                }
              >
                <div className="product-image-wrapper">
                  <button
                    className={`favorite-btn ${
                      isWishlisted ? "active" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleWishlist(item.id);
                    }}
                  >
                    <FaHeart />
                  </button>

                  <img
                    src={item.images?.[0] || "/placeholder.png"}
                    alt={item.product_name}
                    className="product-image"
                  />
                </div>

                <div className="product-info">
                  <h3 className="product-name">
                    {item.product_name}
                  </h3>

                  <div className="product-footer">
                    <div className="product-price-wrapper">
                      {item.cost_price && (
                        <span className="product-cost-price">
                          ₹{item.cost_price}
                        </span>
                      )}
                      <span className="product-selling-price">
                        ₹{item.selling_price}
                      </span>
                    </div>

                    <button
                      className="add-btn"
                      onClick={(e) =>
                        handleAddToCart(e, item.id)
                      }
                      disabled={addingId === item.id}
                    >
                      {addingId === item.id ? "..." : "+"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TopSelling;
